import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { API, colors } from "../shared/constants";
import { styles, inputStyle, labelStyle } from "../shared/styles";
import { LuFolder, LuPlus, LuUpload, LuFileText } from "react-icons/lu";
import { Breadcrumbs } from "./Breadcrumbs";
import { FolderContentsGrid } from "./FolderContentsGrid";
import { MoveCopyModal } from "./MoveCopyModal";

function formatDate(dt) {
  if (!dt) return "";
  return new Date(dt).toLocaleDateString("en-AU", { day: "2-digit", month: "short", year: "numeric" });
}

const REPORT_TYPE_LABELS = {
  hazard: "Hazard Report",
  parking: "Parking Note",
  change: "Change Report",
  service_note: "Service Note",
  service_request: "Service Request",
};

const REPORT_TYPE_COLORS = {
  hazard:          { bg: "#fee2e2", color: "#b91c1c" },
  service_request: { bg: "#ccfbf1", color: "#0f766e" },
  parking:         { bg: "#f1f5f9", color: "#475569" },
  change:          { bg: "#fff7ed", color: "#c2410c" },
  service_note:    { bg: "#dbeafe", color: "#1d4ed8" },
};

const isReportsFolder = (folder) =>
  folder?.name === "Field Reports" ||
  /^Field Reports \d{4}$/.test(folder?.name);

export function RecordsPage() {
  const userRole = localStorage.getItem("sfp_role");
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState(null);
  const [folderPath, setFolderPath] = useState([]); // {id, name, is_system}[], root-first
  const [childFolders, setChildFolders] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [fieldReports, setFieldReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [loadingSites, setLoadingSites] = useState(true);
  const [loadingFolders, setLoadingFolders] = useState(false);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderError, setNewFolderError] = useState("");
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [siteSearch, setSiteSearch] = useState("");
  const [hoveredSite, setHoveredSite] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { type: "folder"|"doc", id }
  const [fileMoveModal, setFileMoveModal] = useState(null); // { item, mode: "move"|"copy", kind: "doc"|"folder", excludeFolderId? }
  const [processingFile, setProcessingFile] = useState(false);
  const fileInputRef = useRef(null);

  const currentFolder = folderPath[folderPath.length - 1] || null;
  const isReports = isReportsFolder(currentFolder);

  useEffect(() => {
    axios.get(`${API}/sites/`).then((res) => {
      setSites(res.data);
      setLoadingSites(false);
    }).catch(() => setLoadingSites(false));
  }, []);

  const loadLevel = (site, path) => {
    const parent = path[path.length - 1];

    if (isReportsFolder(parent)) {
      setChildFolders([]);
      setDocuments([]);
      setLoadingReports(true);
      axios.get(`${API}/field-reports/?site=${site.id}`)
        .then((res) => {
          const yearMatch = parent.name.match(/(\d{4})$/);
          const filtered = yearMatch
            ? res.data.filter((r) => new Date(r.submitted_at).getFullYear() === Number(yearMatch[1]))
            : res.data;
          setFieldReports(filtered);
          setLoadingReports(false);
        })
        .catch(() => setLoadingReports(false));
      return;
    }

    setFieldReports([]);
    setLoadingFolders(true);
    const parentParam = parent ? `&parent=${parent.id}` : "";
    axios.get(`${API}/site-folders/?site=${site.id}${parentParam}`)
      .then((res) => {
        setChildFolders(res.data);
        setLoadingFolders(false);
      })
      .catch(() => setLoadingFolders(false));

    if (parent) {
      setLoadingDocs(true);
      axios.get(`${API}/site-documents/?folder=${parent.id}`)
        .then((res) => {
          setDocuments(res.data);
          setLoadingDocs(false);
        })
        .catch(() => setLoadingDocs(false));
    } else {
      setDocuments([]);
    }
  };

  const openSite = (site) => {
    setSelectedSite(site);
    setFolderPath([]);
    setChildFolders([]);
    setDocuments([]);
    setFieldReports([]);
    loadLevel(site, []);
  };

  const goHome = () => {
    setSelectedSite(null);
    setFolderPath([]);
    setChildFolders([]);
    setDocuments([]);
    setFieldReports([]);
  };

  const openFolder = (folder) => {
    const next = [...folderPath, { id: folder.id, name: folder.name, is_system: folder.is_system }];
    setFolderPath(next);
    loadLevel(selectedSite, next);
  };

  const navigateToIndex = (index) => {
    const next = index < 0 ? [] : folderPath.slice(0, index + 1);
    setFolderPath(next);
    loadLevel(selectedSite, next);
  };

  const refreshLevel = () => loadLevel(selectedSite, folderPath);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length || !currentFolder) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("folder", currentFolder.id);
      try {
        await axios.post(`${API}/site-documents/`, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (err) {
        console.error("Upload failed", file.name, err);
      }
    }
    e.target.value = "";
    setUploading(false);
    refreshLevel();
  };

  const handleCreateFolder = async () => {
    if (!newFolderName.trim() || !selectedSite) return;
    setCreatingFolder(true);
    setNewFolderError("");
    try {
      await axios.post(`${API}/site-folders/`, {
        site: selectedSite.id,
        parent: currentFolder ? currentFolder.id : null,
        name: newFolderName.trim(),
      });
      setNewFolderName("");
      setShowNewFolder(false);
      refreshLevel();
    } catch (err) {
      setNewFolderError(err.response?.data?.name?.[0] || "Create folder failed.");
    }
    setCreatingFolder(false);
  };

  const handleDeleteDoc = async (docId) => {
    try {
      await axios.delete(`${API}/site-documents/${docId}/`);
      refreshLevel();
    } catch (err) {
      window.alert(err.response?.data?.detail || "Delete failed.");
    }
    setDeleteConfirm(null);
  };

  const handleDeleteFolder = async (folderId) => {
    try {
      await axios.delete(`${API}/site-folders/${folderId}/`);
      refreshLevel();
    } catch (err) {
      window.alert(err.response?.data?.detail || "Delete failed.");
    }
    setDeleteConfirm(null);
  };

  const toggleLock = async (doc) => {
    try {
      await axios.patch(`${API}/site-documents/${doc.id}/`, { locked: !doc.locked });
      refreshLevel();
    } catch (err) {
      window.alert(err.response?.data?.detail || "Failed to update lock status.");
    }
  };

  const handleRenameFolder = async (folder, newName) => {
    try {
      await axios.patch(`${API}/site-folders/${folder.id}/`, { name: newName });
      refreshLevel();
    } catch (err) {
      window.alert(err.response?.data?.name?.[0] || err.response?.data?.detail || "Rename failed.");
    }
  };

  const handleRenameDoc = async (doc, newName) => {
    try {
      await axios.patch(`${API}/site-documents/${doc.id}/`, { name: newName });
      refreshLevel();
    } catch (err) {
      window.alert(err.response?.data?.detail || "Rename failed.");
    }
  };

  const handleMoveOrCopy = async (targetFolderId) => {
    if (!fileMoveModal) return;
    const { item, mode, kind } = fileMoveModal;
    if (kind !== "folder" && targetFolderId == null) return;
    setProcessingFile(true);
    try {
      if (kind === "folder") {
        await axios.patch(`${API}/site-folders/${item.id}/`, { parent: targetFolderId });
      } else if (mode === "move") {
        await axios.patch(`${API}/site-documents/${item.id}/`, { folder: targetFolderId });
      } else {
        await axios.post(`${API}/site-documents/${item.id}/copy/`, { folder: targetFolderId });
      }
      setFileMoveModal(null);
      refreshLevel();
    } catch (err) {
      window.alert(err.response?.data?.detail || err.response?.data?.parent?.[0] || "Operation failed.");
    }
    setProcessingFile(false);
  };

  const filteredSites = sites.filter((s) =>
    s.site_name.toLowerCase().includes(siteSearch.toLowerCase()) ||
    s.site_address.toLowerCase().includes(siteSearch.toLowerCase())
  );

  // ── Delete confirm modal ─────────────────────────────────────
  const deleteModal = deleteConfirm && (
    <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
      <div style={{ backgroundColor: colors.white, borderRadius: "12px", padding: "28px", width: "340px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
        <div style={{ fontSize: "16px", fontWeight: "700", color: colors.text, marginBottom: "10px" }}>
          {deleteConfirm.type === "doc" ? "Delete file?" : "Delete folder?"}
        </div>
        <div style={{ fontSize: "14px", color: colors.slate, marginBottom: "24px" }}>
          {deleteConfirm.type === "doc"
            ? "This file will be permanently removed."
            : "This folder and everything inside it (including subfolders) will be permanently deleted."}
        </div>
        <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
          <button onClick={() => setDeleteConfirm(null)} style={{ padding: "8px 18px", borderRadius: "8px", border: `1px solid ${colors.border}`, background: "white", cursor: "pointer", fontSize: "14px" }}>
            Cancel
          </button>
          <button
            onClick={() => deleteConfirm.type === "doc" ? handleDeleteDoc(deleteConfirm.id) : handleDeleteFolder(deleteConfirm.id)}
            style={{ padding: "8px 18px", borderRadius: "8px", border: "none", background: colors.red, color: "white", cursor: "pointer", fontSize: "14px", fontWeight: "600" }}
          >
            Delete
          </button>
        </div>
      </div>
    </div>
  );

  const moveCopyModal = (
    <MoveCopyModal
      fileMoveModal={fileMoveModal}
      siteId={selectedSite?.id}
      currentFolderId={currentFolder?.id ?? null}
      excludeFolderId={fileMoveModal?.kind === "folder" ? fileMoveModal.excludeFolderId : null}
      onCancel={() => setFileMoveModal(null)}
      onConfirm={handleMoveOrCopy}
      processingFile={processingFile}
    />
  );

  // ── Level 1: Site picker ─────────────────────────────────────
  if (!selectedSite) return (
    <div>
      {deleteModal}
      <div style={styles.topbar}>
        <div style={styles.pageTitle}>Records</div>
      </div>
      <div style={styles.content}>
        <div style={styles.card}>
          <div style={styles.searchBar}>
            <input
              style={{ ...styles.searchInput, maxWidth: "50%" }}
              placeholder="Search sites..."
              value={siteSearch}
              onChange={(e) => setSiteSearch(e.target.value)}
            />
          </div>
          {loadingSites ? (
            <div style={styles.loading}>Loading sites...</div>
          ) : filteredSites.length === 0 ? (
            <div style={styles.loading}>No sites found.</div>
          ) : (
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: "16px", padding: "20px" }}>
              {filteredSites.map((site) => (
                <div
                  key={site.id}
                  onClick={() => openSite(site)}
                  onMouseEnter={() => setHoveredSite(site.id)}
                  onMouseLeave={() => setHoveredSite(null)}
                  style={{
                    backgroundColor: hoveredSite === site.id ? "#f0f4ff" : colors.white,
                    border: `1px solid ${hoveredSite === site.id ? colors.blue : colors.border}`,
                    borderRadius: "10px",
                    padding: "18px 16px",
                    cursor: "pointer",
                    transition: "all 0.15s",
                    display: "flex",
                    alignItems: "flex-start",
                    gap: "14px",
                  }}
                >
                  <LuFolder size={28} style={{ color: "#C8922A", flexShrink: 0, marginTop: "2px" }} />
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontWeight: "700", fontSize: "14px", color: colors.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {site.site_name}
                    </div>
                    <div style={{ fontSize: "12px", color: colors.slate, marginTop: "3px", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {site.site_address}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div style={styles.count}>{filteredSites.length} site{filteredSites.length !== 1 ? "s" : ""}</div>
        </div>
      </div>
    </div>
  );

  const breadcrumb = (
    <Breadcrumbs
      siteName={selectedSite.site_name}
      path={folderPath}
      onNavigateHome={goHome}
      onNavigate={navigateToIndex}
    />
  );

  // ── Level: Field Reports (virtual folder, never has subfolders/documents) ──
  if (isReports) return (
    <div>
      {deleteModal}
      <div style={styles.topbar}>
        <div>{breadcrumb}</div>
        <span style={{ fontSize: "13px", color: colors.slate, fontStyle: "italic" }}>
          Submitted by field techs
        </span>
      </div>
      <div style={styles.content}>
        <div style={styles.card}>
          {loadingReports ? (
            <div style={styles.loading}>Loading reports...</div>
          ) : fieldReports.length === 0 ? (
            <div style={{ ...styles.loading, paddingTop: "48px", paddingBottom: "48px" }}>
              <LuFileText size={40} style={{ color: colors.border, display: "block", margin: "0 auto 12px" }} />
              <div>No field reports for this site yet.</div>
            </div>
          ) : (
            <>
              <div style={{ padding: "16px 20px", display: "flex", flexDirection: "column", gap: "12px" }}>
                {fieldReports.map((report) => {
                  const typeColors = REPORT_TYPE_COLORS[report.report_type] || { bg: colors.lightGrey, color: colors.slate };
                  const typeLabel = REPORT_TYPE_LABELS[report.report_type] || report.report_type;
                  return (
                    <div
                      key={report.id}
                      style={{
                        border: `1px solid ${colors.border}`,
                        borderRadius: "10px",
                        padding: "16px 18px",
                        backgroundColor: colors.white,
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "flex-start", gap: "10px", flexWrap: "wrap", marginBottom: "8px" }}>
                        <span style={{
                          fontSize: "11px", fontWeight: "700", padding: "3px 10px", borderRadius: "20px",
                          backgroundColor: typeColors.bg, color: typeColors.color, whiteSpace: "nowrap",
                        }}>
                          {typeLabel}
                        </span>
                        <span style={{
                          fontSize: "11px", fontWeight: "600", padding: "3px 10px", borderRadius: "20px",
                          backgroundColor: report.is_resolved ? "#dcfce7" : "#fff7ed",
                          color: report.is_resolved ? "#15803d" : "#c2410c",
                          whiteSpace: "nowrap",
                        }}>
                          {report.is_resolved ? "Resolved" : "Unresolved"}
                        </span>
                      </div>
                      {report.title && (
                        <div style={{ fontWeight: "700", fontSize: "14px", color: colors.text, marginBottom: "4px" }}>
                          {report.title}
                        </div>
                      )}
                      {report.description && (
                        <div style={{ fontSize: "13px", color: colors.slate, marginBottom: "10px", lineHeight: "1.5" }}>
                          {report.description}
                        </div>
                      )}
                      <div style={{ fontSize: "12px", color: colors.slate }}>
                        {report.submitted_by_name || "Field Tech"} &middot; {formatDate(report.submitted_at)}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div style={styles.count}>{fieldReports.length} report{fieldReports.length !== 1 ? "s" : ""}</div>
            </>
          )}
        </div>
      </div>
    </div>
  );

  // ── Level: Folder contents (subfolders + documents together) ─────────
  const isEmpty = childFolders.length === 0 && documents.length === 0;
  const loading = loadingFolders || loadingDocs;

  return (
    <div>
      {deleteModal}
      {moveCopyModal}
      <div style={styles.topbar}>
        <div>{breadcrumb}</div>
        <div style={{ display: "flex", gap: "10px" }}>
          <button onClick={() => setShowNewFolder(true)} style={styles.addButton}>
            <LuPlus size={15} /> New Folder
          </button>
          {currentFolder && (
            <>
              <input
                type="file"
                multiple
                ref={fileInputRef}
                style={{ display: "none" }}
                onChange={handleUpload}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
                style={{ ...styles.addButton, opacity: uploading ? 0.7 : 1 }}
              >
                <LuUpload size={15} /> {uploading ? "Uploading..." : "Upload File"}
              </button>
            </>
          )}
        </div>
      </div>
      <div style={styles.content}>
        <div style={styles.card}>
          {loading ? (
            <div style={styles.loading}>Loading...</div>
          ) : isEmpty ? (
            <div style={{ ...styles.loading, paddingTop: "48px", paddingBottom: "48px" }}>
              <LuFolder size={40} style={{ color: colors.border, display: "block", margin: "0 auto 12px" }} />
              <div>
                {currentFolder
                  ? "This folder is empty — add a subfolder or upload a file."
                  : "No folders yet — click New Folder to add one."}
              </div>
            </div>
          ) : (
            <>
              <FolderContentsGrid
                folders={childFolders}
                documents={documents}
                userRole={userRole}
                onOpenFolder={openFolder}
                onDeleteFolder={(id) => setDeleteConfirm({ type: "folder", id })}
                onRenameFolder={handleRenameFolder}
                onMoveFolder={(folder) => setFileMoveModal({ item: folder, mode: "move", kind: "folder", excludeFolderId: folder.id })}
                onToggleLock={toggleLock}
                onMoveDoc={(doc) => setFileMoveModal({ item: doc, mode: "move", kind: "doc" })}
                onCopyDoc={(doc) => setFileMoveModal({ item: doc, mode: "copy", kind: "doc" })}
                onDeleteDoc={(id) => setDeleteConfirm({ type: "doc", id })}
                onRenameDoc={handleRenameDoc}
              />
              <div style={styles.count}>
                {childFolders.length} folder{childFolders.length !== 1 ? "s" : ""}, {documents.length} file{documents.length !== 1 ? "s" : ""}
              </div>
            </>
          )}
        </div>
      </div>

      {/* New folder modal */}
      {showNewFolder && (
        <div style={{ position: "fixed", inset: 0, backgroundColor: "rgba(0,0,0,0.4)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <div style={{ backgroundColor: colors.white, borderRadius: "12px", padding: "28px", width: "360px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)" }}>
            <div style={{ fontSize: "16px", fontWeight: "700", color: colors.text, marginBottom: "16px" }}>New Folder</div>
            <div style={{ marginBottom: "20px" }}>
              <label style={labelStyle}>Folder Name *</label>
              <input
                style={inputStyle}
                value={newFolderName}
                onChange={(e) => { setNewFolderName(e.target.value); setNewFolderError(""); }}
                onKeyDown={(e) => e.key === "Enter" && handleCreateFolder()}
                placeholder="e.g. Certificates"
                autoFocus
              />
              {newFolderError && (
                <div style={{ fontSize: "12px", color: colors.red, marginTop: "6px" }}>{newFolderError}</div>
              )}
            </div>
            <div style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
              <button onClick={() => { setShowNewFolder(false); setNewFolderName(""); setNewFolderError(""); }} style={{ padding: "8px 18px", borderRadius: "8px", border: `1px solid ${colors.border}`, background: "white", cursor: "pointer", fontSize: "14px" }}>
                Cancel
              </button>
              <button onClick={handleCreateFolder} disabled={creatingFolder || !newFolderName.trim()} style={{ ...styles.addButton, padding: "8px 18px" }}>
                {creatingFolder ? "Creating..." : "Create"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
